import { Spacing } from '@persona-id/design-tokens';
import { ClipboardIcon } from '@persona-id/design-tokens/icons';
import { MouseEvent, ReactNode } from 'react';
import styled from 'styled-components';

import copyToClipboard from 'dashboard/lib/utils/copyToClipboard';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Popover from 'design-system/components/Popover';
import { Paragraph } from 'design-system/components/Text';
import { fadeIn } from 'design-system/variables/keyframes';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';

const CopyableFieldContainer = styled.div`
  display: flex;
  gap: ${Spacing[1]};
  align-items: center;
`;

const CopyButton = styled(Button).attrs(() => ({
  type: 'button',
  variant: 'inverse',
  width: 'icon',
}))<{ isActive: boolean }>`
  height: ${Spacing[5]};
  width: ${Spacing[5]};
  visibility: ${({ isActive }) => (isActive ? 'visible' : 'hidden')};
  animation: ${fadeIn} ${TransitionDuration.Medium} ease-in-out;
  svg {
    width: 14px;
    height: 14px;
  }
`;

const StyledCard = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  animation: ${fadeIn} ${TransitionDuration.Medium} ease-in-out;
  padding: 6px;
  margin: 0;
`;

interface HoverCopyableFieldProps {
  copyValue: string | undefined;
  confirmationText?: string;
  children: ReactNode;
}

const HoverCopyableField = ({
  copyValue,
  confirmationText = 'Copied',
  children,
}: HoverCopyableFieldProps) => {
  const [copyButtonActive, { enable: showCopyButton, disable: hideCopyButton }] = useToggle(false);
  const [confirmationActive, { enable: showConfirmation, disable: hideConfirmation }] =
    useToggle(false);

  const copyTextToClipboard = async () => {
    if (copyValue == null) return;
    const copyWasSuccessful = await copyToClipboard(copyValue);

    if (copyWasSuccessful) {
      showConfirmation();
      setTimeout(() => {
        hideCopyButton();
        hideConfirmation();
      }, 1200);
    }
  };

  const copyField = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    copyTextToClipboard();
  };

  return copyValue != null && copyValue.trim().length > 0 ? (
    <CopyableFieldContainer onMouseEnter={showCopyButton} onMouseLeave={hideCopyButton}>
      {children}
      <Popover
        open={confirmationActive}
        trigger={
          <CopyButton
            id='hover-copyable-field__copy-button'
            isActive={copyButtonActive || confirmationActive}
            onClick={copyField}
          >
            <ClipboardIcon width={16} height={16} />
          </CopyButton>
        }
      >
        <Popover.Content side='top' align='center' style={{ zIndex: 2 }}>
          <StyledCard elevated>
            <Paragraph>{confirmationText}</Paragraph>
          </StyledCard>
        </Popover.Content>
      </Popover>
    </CopyableFieldContainer>
  ) : null;
};

export default HoverCopyableField;
